import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect path if coming from a protected route
  const from = location.state?.from?.pathname || '/dashboard';

  const getErrorMessage = (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;

    if (status === 401) {
      return 'Invalid email or password. Please try again.';
    }
    if (status === 403) {
      return detail || 'Account is suspended. Contact your administrator.';
    }
    if (status === 422) {
      return 'Please enter a valid email address.';
    }
    if (status === 500) {
      return 'Server error. Please try again later.';
    }
    if (!error.response) {
      return 'Unable to connect. Please check your internet connection.';
    }
    return detail || 'Login failed. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      if (user.is_temp_password) {
        navigate('/change-password', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="login-page">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
          Welcome back
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Sign in to your account to continue
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-4 bg-red-50 border-red-200" data-testid="login-error">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-600 text-sm">
            {error}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-slate-900">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            required
            disabled={isLoading}
            className="h-10"
            data-testid="login-email-input"
            autoComplete="email"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium text-slate-900">
            Password
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              required
              disabled={isLoading}
              className="h-10 pr-10"
              data-testid="login-password-input"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          className="w-full h-10 bg-slate-900 text-white hover:bg-slate-800 font-medium"
          disabled={isLoading}
          data-testid="login-submit-button"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign in'
          )}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-slate-500">
        Want to set up your organization?{' '}
        <Link
          to="/create-organization"
          className="font-medium text-slate-900 hover:underline underline-offset-4"
          data-testid="create-org-link"
        >
          Create an organization
        </Link>
      </p>
    </div>
  );
};

export default LoginPage;
